import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader, handleApiError } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Plus, Edit2, Trash2, Package, RefreshCw, Users, TrendingUp, Star, Percent, AlertTriangle, Download, Building2, Calculator, DollarSign } from 'lucide-react';

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function PlanosPage() {
  const [planos, setPlanos] = React.useState([]);
  const [clientesPorPlano, setClientesPorPlano] = React.useState({});
  const [planosFaltantes, setPlanosFaltantes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [syncing, setSyncing] = React.useState(false);
  const [criandoPlanos, setCriandoPlanos] = React.useState(false);
  const [importandoValores, setImportandoValores] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [selectedPlano, setSelectedPlano] = React.useState(null);
  const [formData, setFormData] = React.useState({ nome: '', valor: '', custo: '', desconto_combo: '', descricao: '' });
  const [filiais, setFiliais] = React.useState([]);
  const [custoMegaData, setCustoMegaData] = React.useState([]);
  const [custoMegaModalOpen, setCustoMegaModalOpen] = React.useState(false);
  const [calculandoCustos, setCalculandoCustos] = React.useState(false);

  React.useEffect(function() { 
    fetchPlanos(); 
    fetchClientesPorPlano();
    fetchPlanosFaltantes();
    fetchFiliais();
    fetchCustoMega();
  }, []);

  function fetchFiliais() {
    axios.get(API + '/filiais', { headers: getAuthHeader() })
      .then(function(response) { setFiliais(response.data); })
      .catch(function(error) { console.error('Erro ao carregar filiais'); });
  }

  function fetchCustoMega() {
    axios.get(API + '/filiais/custo-mega', { headers: getAuthHeader() })
      .then(function(response) { setCustoMegaData(response.data); })
      .catch(function(error) { console.error('Erro ao carregar custo mega'); });
  }

  function atualizarCustoLinkFilial(filialId, custoLink) {
    axios.put(API + '/filiais/' + filialId, { custo_link: parseFloat(custoLink) || 0 }, { headers: getAuthHeader() })
      .then(function() { 
        toast.success('Custo do link atualizado');
        fetchFiliais();
        fetchCustoMega();
      })
      .catch(function(error) { handleApiError(error); toast.error('Erro ao atualizar'); });
  }

  function calcularCustosPlanos() {
    setCalculandoCustos(true);
    axios.post(API + '/planos/calcular-custos', {}, { headers: getAuthHeader() })
      .then(function(response) { 
        toast.success(response.data.message);
        fetchPlanos();
        fetchCustoMega();
      })
      .catch(function(error) { handleApiError(error); toast.error('Erro ao calcular custos'); })
      .finally(function() { setCalculandoCustos(false); });
  }

  function fetchPlanos() {
    setLoading(true);
    // Usar o novo endpoint que já retorna planos ordenados com contagem de clientes
    axios.get(API + '/planos/com-clientes', { headers: getAuthHeader() })
      .then(function(response) { 
        setPlanos(response.data);
        // Extrair contagem de clientes dos planos
        var porPlano = {};
        response.data.forEach(function(p) {
          porPlano[p.nome] = p.total_clientes || 0;
        });
        setClientesPorPlano(porPlano);
      })
      .catch(function(error) { 
        // Fallback para o endpoint antigo se o novo falhar
        handleApiError(error); 
        toast.error('Erro ao carregar planos'); 
      })
      .finally(function() { setLoading(false); });
  }

  function fetchClientesPorPlano() {
    // Não é mais necessário - a contagem já vem no fetchPlanos
    // Mantido para compatibilidade, mas não faz nada
  }

  function fetchPlanosFaltantes() {
    axios.get(API + '/planos/faltantes', { headers: getAuthHeader() })
      .then(function(response) {
        setPlanosFaltantes(response.data.planos_faltantes || []);
      })
      .catch(function(error) { console.error('Erro ao buscar planos faltantes:', error); });
  }

  function criarPlanosFaltantes() {
    setCriandoPlanos(true);
    axios.post(API + '/planos/criar-faltantes', {}, { headers: getAuthHeader() })
      .then(function(response) {
        toast.success(response.data.message);
        fetchPlanos();
        fetchPlanosFaltantes();
      })
      .catch(function(error) { handleApiError(error); toast.error('Erro ao criar planos'); })
      .finally(function() { setCriandoPlanos(false); });
  }

  function importarValoresIXC() {
    setImportandoValores(true);
    axios.post(API + '/ixc/importar-valores-planos', {}, { headers: getAuthHeader() })
      .then(function(response) {
        var msg = response.data.planos_atualizados + ' planos atualizados, ' + response.data.clientes_atualizados + ' clientes atualizados';
        toast.success(msg);
        if (response.data.planos_nao_encontrados && response.data.planos_nao_encontrados.length > 0) {
          toast.warning(response.data.planos_nao_encontrados.length + ' planos não encontrados no IXC - definir valores manualmente');
        }
        fetchPlanos();
      })
      .catch(function(error) { handleApiError(error); toast.error('Erro ao importar valores'); })
      .finally(function() { setImportandoValores(false); });
  }

  function openCreateModal() {
    setSelectedPlano(null);
    setFormData({ nome: '', valor: '', custo: '', desconto_combo: '', descricao: '' });
    setModalOpen(true);
  }

  function openEditModal(plano) {
    setSelectedPlano(plano);
    setFormData({ nome: plano.nome, valor: plano.valor, custo: plano.custo, desconto_combo: plano.desconto_combo || '', descricao: plano.descricao || '' });
    setModalOpen(true);
  }

  function handleSubmit(e) {
    e.preventDefault();
    var data = { 
      nome: formData.nome, 
      valor: parseFloat(formData.valor) || 0, 
      custo: parseFloat(formData.custo) || 0, 
      desconto_combo: parseFloat(formData.desconto_combo) || 0,
      descricao: formData.descricao 
    };
    var request = selectedPlano
      ? axios.put(API + '/planos/' + selectedPlano.id, data, { headers: getAuthHeader() })
      : axios.post(API + '/planos', data, { headers: getAuthHeader() });
    request.then(function() {
      toast.success(selectedPlano ? 'Plano atualizado! Clientes atualizados automaticamente.' : 'Plano criado!');
      setModalOpen(false);
      fetchPlanos();
    }).catch(function() { toast.error('Erro ao salvar'); });
  }

  function handleDelete() {
    axios.delete(API + '/planos/' + selectedPlano.id, { headers: getAuthHeader() })
      .then(function() { toast.success('Plano excluído!'); setDeleteModalOpen(false); fetchPlanos(); })
      .catch(function() { toast.error('Erro ao excluir'); });
  }

  function sincronizarClientes() {
    setSyncing(true);
    axios.post(API + '/planos/sincronizar-clientes', {}, { headers: getAuthHeader() })
      .then(function(response) {
        toast.success(response.data.message);
      })
      .catch(function() { toast.error('Erro ao sincronizar'); })
      .finally(function() { setSyncing(false); });
  }

  function updateForm(field, value) {
    setFormData(function(prev) { var newData = Object.assign({}, prev); newData[field] = value; return newData; });
  }

  // Find most popular plan
  var planoMaisPopular = '';
  var maxClientes = 0;
  Object.keys(clientesPorPlano).forEach(function(plano) {
    if (clientesPorPlano[plano] > maxClientes) {
      maxClientes = clientesPorPlano[plano];
      planoMaisPopular = plano;
    }
  });

  return (
    <div className="space-y-6" data-testid="planos-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">PLANOS</h1>
          <p className="text-slate-500 text-sm">{planos.length} planos cadastrados</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={importarValoresIXC} disabled={importandoValores} variant="outline" size="sm" className="bg-white border-blue-300 text-blue-600 hover:bg-blue-50">
            <Download className={importandoValores ? "w-4 h-4 sm:mr-2 animate-pulse" : "w-4 h-4 sm:mr-2"} />
            <span className="hidden sm:inline">{importandoValores ? 'Importando...' : 'Importar IXC'}</span>
          </Button>
          <Button onClick={sincronizarClientes} disabled={syncing} variant="outline" size="sm" className="bg-white border-slate-300 text-slate-600 hover:bg-slate-50">
            <RefreshCw className={syncing ? "w-4 h-4 sm:mr-2 animate-spin" : "w-4 h-4 sm:mr-2"} />
            <span className="hidden sm:inline">{syncing ? 'Sincronizando...' : 'Atualizar'}</span>
          </Button>
          <Button data-testid="add-plano-btn" onClick={openCreateModal} size="sm" className="bg-slate-800 hover:bg-slate-700 text-white">
            <Plus className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Novo</span>
          </Button>
        </div>
      </div>

      <Card className="bg-orange-50 border-orange-200">
        <CardContent className="py-3 px-4">
          <p className="text-orange-700 text-sm">
            <strong>💡 Dica:</strong> Ao editar um plano, os valores de todos os clientes com esse plano serão atualizados automaticamente. 
            Use o botão "Atualizar Clientes" para sincronizar clientes existentes que ainda não têm valores definidos.
          </p>
        </CardContent>
      </Card>

      {planosFaltantes.length > 0 && (
        <Card className="bg-red-50 border-red-300">
          <CardContent className="py-4 px-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="text-red-700 font-semibold">
                    {planosFaltantes.length} planos não cadastrados encontrados!
                  </p>
                  <p className="text-red-600 text-sm">
                    Clientes com esses planos não terão valores calculados. 
                    Planos: {planosFaltantes.slice(0, 3).map(function(p) { return p.nome; }).join(', ')}
                    {planosFaltantes.length > 3 && ' e mais ' + (planosFaltantes.length - 3) + '...'}
                  </p>
                </div>
              </div>
              <Button 
                onClick={criarPlanosFaltantes} 
                disabled={criandoPlanos} 
                className="bg-red-600 hover:bg-red-700 text-white whitespace-nowrap"
              >
                <Plus className="w-4 h-4 mr-2" />
                {criandoPlanos ? 'Criando...' : 'Criar Planos Faltantes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seção de Custo por Filial para Planos Fibra */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="py-4 px-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Calculator className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-white font-semibold">Cálculo de Custo por Cliente - Planos Fibra</p>
                <p className="text-slate-400 text-sm">
                  Fórmula: Custo do Link ÷ Total de Clientes = Custo do Plano
                </p>
              </div>
            </div>
            
            {/* Cards de resultado por filial */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {custoMegaData.map(function(filial) {
                return (
                  <div key={filial.filial_id} className="bg-slate-700 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white font-bold flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-400" />
                        {filial.filial_nome} ({filial.filial_codigo})
                      </span>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Custo do Link:</span>
                        <span className="text-orange-400 font-semibold">{formatCurrency(filial.custo_link)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Total Clientes:</span>
                        <span className="text-blue-400 font-semibold">{filial.qtd_clientes}</span>
                      </div>
                      <div className="flex justify-between border-t border-slate-600 pt-2 mt-2">
                        <span className="text-slate-300 font-medium">Custo por Cliente:</span>
                        <span className="text-emerald-400 font-bold text-lg">{formatCurrency(filial.custo_por_cliente)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-end">
              <Button 
                onClick={function() { setCustoMegaModalOpen(true); }} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Editar Custos dos Links
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Configuração de Custo por Filial */}
      <Dialog open={custoMegaModalOpen} onOpenChange={setCustoMegaModalOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-blue-600" />
              Editar Custo do Link por Filial
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-600 text-sm">
              Informe o custo mensal do link de internet de cada filial. O custo por cliente será calculado automaticamente.
            </p>
            
            {filiais.map(function(filial) {
              var custoLink = filial.custo_link || 0;
              var custoMegaInfo = custoMegaData.find(function(c) { return c.filial_id === filial.id; }) || {};
              
              return (
                <div key={filial.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        {filial.nome} ({filial.codigo})
                      </p>
                      <p className="text-sm text-slate-500">
                        {custoMegaInfo.qtd_clientes || 0} clientes Fibra
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div>
                        <Label className="text-slate-600 text-xs">Custo do Link (R$)</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          defaultValue={custoLink}
                          className="w-32 bg-white border-slate-300"
                          onBlur={function(e) { 
                            if (parseFloat(e.target.value) !== custoLink) {
                              atualizarCustoLinkFilial(filial.id, e.target.value);
                            }
                          }}
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500">Custo/Cliente</p>
                        <p className="text-lg font-bold text-emerald-600">{formatCurrency(custoMegaInfo.custo_por_cliente || 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            <div className="flex justify-between items-center pt-4 border-t border-slate-200">
              <p className="text-slate-500 text-sm">
                Clique em "Aplicar aos Planos" para atualizar o custo de todos os planos Fibra.
              </p>
              <Button 
                onClick={calcularCustosPlanos} 
                disabled={calculandoCustos}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Calculator className="w-4 h-4 mr-2" />
                {calculandoCustos ? 'Calculando...' : 'Aplicar aos Planos'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {planos.map(function(plano) {
          var numClientes = clientesPorPlano[plano.nome] || 0;
          var lucro = plano.valor - plano.custo;
          var isPopular = plano.nome === planoMaisPopular && maxClientes > 0;
          
          return (
            <Card key={plano.id} className={'bg-white border-2 shadow-sm hover:shadow-lg transition-all duration-300 ' + (isPopular ? 'border-orange-400 ring-2 ring-orange-200' : 'border-slate-200 hover:border-slate-800')}>
              {/* Header colorido */}
              <div className={'h-2 ' + (isPopular ? 'bg-gradient-to-r from-orange-500 to-orange-400' : 'bg-gradient-to-r from-slate-800 to-slate-600')}></div>
              
              <CardContent className="p-5">
                {/* Título e ações */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ' + (isPopular ? 'bg-orange-500' : 'bg-slate-800')}>
                      <Package className="w-6 h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-xl font-bold text-slate-800 truncate">{plano.nome}</h3>
                      {isPopular && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                          <Star className="w-3 h-3" /> Mais vendido
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 ml-2">
                    <Button onClick={function() { openEditModal(plano); }} variant="outline" size="sm" className="text-slate-600 hover:text-slate-800 hover:bg-slate-100 h-8 w-8 p-0" data-testid={'edit-plano-' + plano.id}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button onClick={function() { setSelectedPlano(plano); setDeleteModalOpen(true); }} variant="outline" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0" data-testid={'delete-plano-' + plano.id}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Número de clientes */}
                <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                  <Users className="w-5 h-5 text-slate-600" />
                  <span className="text-slate-600 text-sm">Clientes:</span>
                  <span className="text-slate-800 font-bold text-lg">{numClientes}</span>
                </div>

                {/* Valores */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 text-sm">Valor</span>
                    <span className="text-slate-800 font-bold text-lg">{formatCurrency(plano.valor)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 text-sm">Custo</span>
                    <span className="text-orange-600 font-semibold">{formatCurrency(plano.custo)}</span>
                  </div>
                  {plano.desconto_combo > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500 text-sm flex items-center gap-1">
                        <Percent className="w-3 h-3" /> Desc. Combo
                      </span>
                      <span className="text-violet-600 font-semibold">-{formatCurrency(plano.desconto_combo)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-3 border-t-2 border-slate-100">
                    <span className="text-slate-500 text-sm flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" /> Lucro
                    </span>
                    <span className={`font-bold text-lg ${lucro >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(lucro)}
                    </span>
                  </div>
                </div>

                {/* Descrição */}
                {plano.descricao && (
                  <p className="text-slate-400 text-sm mt-4 pt-3 border-t border-slate-100 italic">
                    {plano.descricao}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {planos.length === 0 && !loading && (
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-slate-500 text-lg">Nenhum plano cadastrado</p>
            <p className="text-slate-400 text-sm mt-1">Clique em "Novo Plano" para começar</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                <Package className="w-4 h-4 text-white" />
              </div>
              {selectedPlano ? 'Editar' : 'Novo'} Plano
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-600">Nome do Plano *</Label>
              <Input value={formData.nome} onChange={function(e) { updateForm('nome', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="Ex: 10G, 20G, 50G" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600">Valor (R$) *</Label>
                <Input type="number" step="0.01" value={formData.valor} onChange={function(e) { updateForm('valor', e.target.value); }} className="bg-white border-slate-300 text-slate-700" required />
              </div>
              <div>
                <Label className="text-slate-600">Custo (R$) *</Label>
                <Input type="number" step="0.01" value={formData.custo} onChange={function(e) { updateForm('custo', e.target.value); }} className="bg-white border-slate-300 text-slate-700" required />
              </div>
            </div>
            <div>
              <Label className="text-slate-600 flex items-center gap-2">
                <Percent className="w-4 h-4 text-violet-500" />
                Desconto Combo (R$)
              </Label>
              <Input type="number" step="0.01" value={formData.desconto_combo} onChange={function(e) { updateForm('desconto_combo', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="Desconto para clientes combo" />
              <p className="text-xs text-slate-400 mt-1">Desconto aplicado automaticamente para clientes que possuem fibra + móvel</p>
            </div>
            <div>
              <Label className="text-slate-600">Descrição</Label>
              <Input value={formData.descricao} onChange={function(e) { updateForm('descricao', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="Descrição opcional" />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button type="button" onClick={function() { setModalOpen(false); }} variant="outline" className="bg-white border-slate-300 text-slate-600 hover:bg-slate-50">Cancelar</Button>
              <Button type="submit" className="bg-slate-800 hover:bg-slate-700 text-white">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
                <Trash2 className="w-4 h-4 text-white" />
              </div>
              Confirmar Exclusão
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 py-4">
            Tem certeza que deseja excluir o plano <strong>{selectedPlano ? selectedPlano.nome : ''}</strong>?
          </p>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button onClick={function() { setDeleteModalOpen(false); }} variant="outline" className="bg-white border-slate-300 text-slate-600 hover:bg-slate-50">Cancelar</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PlanosPage;
